import { useState, useCallback, useRef, useEffect } from "react";
import { ChapterData } from "../ChapterSection";

export type ImageActionState = {
  images: string[];
  selected: number[];
  removedImages: { index: number; image: string }[];
  operation?: 'merge' | 'cut' | 'resize' | 'selection' | null;
  mergedChapter?: ChapterData;
  originalChapters?: ChapterData[];
  cutPosition?: number;
  originalChapter?: ChapterData;
  splitChapters?: ChapterData[];
  chapters: ChapterData[]; // Required
};

export function useUndoRedo<T extends ImageActionState>(initialState: T) {
  // Store initial state reference that we should never go below
  const initialStateRef = useRef<T>({
    ...initialState,
    chapters: initialState.chapters || []
  });


  const [history, setHistory] = useState<T[]>([initialState]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Flag to prevent unnecessary history updates
  const isUpdatingRef = useRef(false);

  const currentState = history[currentIndex];

  // Update initial chapters without triggering re-renders
  const updateInitialChapters = useCallback((chapters: ChapterData[]) => {
    try {
      if (chapters && chapters.length > 0) {
        initialStateRef.current = {
          ...initialStateRef.current,
          chapters: chapters,
          images: chapters.map(ch => ch.url)
        };

        // Only update history if needed and not already updating
        if (history[0].chapters.length === 0 && !isUpdatingRef.current) {
          isUpdatingRef.current = true;
          setHistory(prev => {
            const newHistory = [...prev];
            newHistory[0] = {
              ...newHistory[0],
              chapters: chapters,
              images: chapters.map(ch => ch.url)
            };
            return newHistory;
          });
          // Reset flag after state update
          setTimeout(() => {
            isUpdatingRef.current = false;
          }, 0);
        }
      }
    } catch (error) {
      console.error(error)
    }
  }, [history]);

  const setState = useCallback((update: T | ((prev: T) => T)) => {
    try {

      if (isUpdatingRef.current) return; // Prevent state updates during internal updates

      isUpdatingRef.current = true;
      setHistory(prevHistory => {
        const updatedState = typeof update === "function"
          ? (update as (prev: T) => T)(prevHistory[currentIndex])
          : update;

        // Important: Ensure chapters is never empty by falling back to initial chapters
        const newState = {
          ...updatedState,
          chapters: (updatedState.chapters && updatedState.chapters.length > 0)
            ? updatedState.chapters
            : initialStateRef.current.chapters
        };

        // Skip if state hasn't changed
        if (JSON.stringify(prevHistory[currentIndex]) === JSON.stringify(newState)) {
          return prevHistory;
        }

        // If we're not at the end of history, discard future states
        const updatedHistory = prevHistory.slice(0, currentIndex + 1);
        return [...updatedHistory, newState];
      });

      setCurrentIndex(prevIndex => prevIndex + 1);

      // Reset flag after state update
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    } catch (error) {
      console.error(error)
    }
  }, [currentIndex]);

  const undo = useCallback(() => {
    try {

      if (currentIndex > 0 && !isUpdatingRef.current) {
        isUpdatingRef.current = true;

        // Move to previous state
        setCurrentIndex(prev => prev - 1);

        // Check if previous state needs chapters
        if (!history[currentIndex - 1].chapters || history[currentIndex - 1].chapters.length === 0) {
          setHistory(prev => {
            const newHistory = [...prev];
            newHistory[currentIndex - 1] = {
              ...newHistory[currentIndex - 1],
              chapters: initialStateRef.current.chapters,
              images: initialStateRef.current.chapters.map(ch => ch.url)
            };
            return newHistory;
          });
        }

        // Reset flag after state update
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
      }

    } catch (error) {
    }
  }, [currentIndex, history]);

  const redo = useCallback(() => {
    try {

      if (currentIndex < history.length - 1 && !isUpdatingRef.current) {
        isUpdatingRef.current = true;

        // Move to next state
        setCurrentIndex(prev => prev + 1);

        // Check if next state needs chapters
        if (!history[currentIndex + 1].chapters || history[currentIndex + 1].chapters.length === 0) {
          setHistory(prev => {
            const newHistory = [...prev];
            newHistory[currentIndex + 1] = {
              ...newHistory[currentIndex + 1],
              chapters: initialStateRef.current.chapters,
              images: initialStateRef.current.chapters.map(ch => ch.url)
            };
            return newHistory;
          });
        }

        // Reset flag after state update
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
      }
    } catch (error) {
      console.error(error)
    }
  }, [currentIndex, history]);

  // Remove dependency on history from the callbacks
  // to prevent infinite rendering cycles
  const undoStable = useCallback(() => {
    if (currentIndex > 0 && !isUpdatingRef.current) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const redoStable = useCallback(() => {
    if (currentIndex < history.length - 1 && !isUpdatingRef.current) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, history.length]);
  return {
    state: currentState,
    setState,
    undo: undo,
    redo: redo,
    updateInitialChapters,
    canUndo: currentIndex > 0,
    canRedo: currentIndex < history.length - 1,
  };
}